import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Grid,
  Alert,
  TextField,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Add, NavigateBefore, NavigateNext, Edit, Delete } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/el';
import isoWeek from 'dayjs/plugin/isoWeek';
import { usePresetHours } from './PresetHoursContext';

dayjs.extend(isoWeek);
dayjs.locale('el');

const Schedule2 = () => {
  const [currentWeek, setCurrentWeek] = useState(dayjs().startOf('isoWeek'));
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedDays, setSelectedDays] = useState([]); // New: for multiple day selection
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedHours, setSelectedHours] = useState('');
  const [customStartTime, setCustomStartTime] = useState('');
  const [customEndTime, setCustomEndTime] = useState('');
  const [useCustomTime, setUseCustomTime] = useState(false);
  const [selectedShiftType, setSelectedShiftType] = useState(''); // 'morning' or 'evening'
  const [editingShift, setEditingShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { presetHours } = usePresetHours();

  const daysOfWeek = ['Δευτέρα', 'Τρίτη', 'Τετάρτη', 'Πέμπτη', 'Παρασκευή', 'Σάββατο', 'Κυριακή'];

  useEffect(() => {
    fetchEmployees();
    fetchShifts();
  }, [currentWeek]);

  const fetchEmployees = async () => {
    try {
      const response = await fetch('/api/employees?active=true');
      if (!response.ok) throw new Error('Failed to fetch employees');
      const data = await response.json();
      console.log('Employees data:', data);
      setEmployees(data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setError('Σφάλμα φόρτωσης υπαλλήλων');
    }
  };

  const fetchShifts = async () => {
    try {
      setLoading(true);
      const weekStart = currentWeek.format('YYYY-MM-DD');
      const url = `/api/shifts?week_start=${weekStart}`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch shifts');
      const data = await response.json();
      console.log('Shifts fetched:', data.length, 'for week:', weekStart);
      console.log('Shifts data:', data);
      
      setShifts(data);
    } catch (error) {
      console.error('Error fetching shifts:', error);
      setError('Σφάλμα φόρτωσης βαρδιών');
    } finally {
      setLoading(false);
    }
  };

  const getShiftsForDay = (dayIndex) => {
    // Convert UI dayIndex to backend day number
    // UI: ['Δευτέρα', 'Τρίτη', 'Τετάρτη', 'Πέμπτη', 'Παρασκευή', 'Σάββατο', 'Κυριακή'] (indexes 0-6)
    // Backend: Sunday=0, Monday=1, Tuesday=2, Wednesday=3, Thursday=4, Friday=5, Saturday=6
    const backendDay = dayIndex === 6 ? 0 : dayIndex + 1; // Sunday is at index 6 in UI but day 0 in backend
    return shifts.filter(shift => shift.day == backendDay);
  };

  const getMorningShifts = (dayIndex) => {
    const shiftsForDay = getShiftsForDay(dayIndex);
    console.log(`Getting morning shifts for day ${dayIndex}:`, shiftsForDay);
    const filtered = shiftsForDay.filter(shift => 
      shift.shift_type === 'morning' || 
      shift.shift_type === 'Πρωινη' ||
      shift.shift_type === 'Εργάσιμη' ||
      shift.shift_type === 'regular'
    );
    console.log(`Filtered morning shifts:`, filtered);
    return filtered;
  };

  const getEveningShifts = (dayIndex) => {
    return getShiftsForDay(dayIndex).filter(shift => 
      shift.shift_type === 'evening' || 
      shift.shift_type === 'Βραδινη' ||
      shift.shift_type === 'Απογευματινή'
    );
  };

  const handleAddShift = (day, shiftType) => {
    setSelectedDay(day);
    setSelectedDays([day]); // Initialize with single day
    setSelectedEmployee('');
    setSelectedHours('');
    setCustomStartTime('');
    setCustomEndTime('');
    setUseCustomTime(false);
    setSelectedShiftType(shiftType);
    setEditingShift(null);
    setOpenModal(true);
  };

  // New function to handle day selection for multiple days
  const handleDayToggle = (dayIndex) => {
    setSelectedDays(prev => {
      if (prev.includes(dayIndex)) {
        return prev.filter(d => d !== dayIndex);
      } else {
        return [...prev, dayIndex];
      }
    });
  };

  const handleEditShift = (shift) => {
    // Convert backend day number to UI index
    // Backend: Sunday=0, Monday=1, Tuesday=2, Wednesday=3, Thursday=4, Friday=5, Saturday=6
    // UI: ['Δευτέρα', 'Τρίτη', 'Τετάρτη', 'Πέμπτη', 'Παρασκευή', 'Σάββατο', 'Κυριακή'] (indexes 0-6)
    const uiDay = shift.day === 0 ? 6 : shift.day - 1;
    
    setSelectedDay(uiDay);
    setSelectedEmployee(shift.employee_id);
    
    // Check if current times match any preset hours
    const currentTimeString = `${shift.start_time}-${shift.end_time}`;
    const matchesPreset = presetHours.includes(currentTimeString);
    
    if (matchesPreset) {
      setSelectedHours(currentTimeString);
      setUseCustomTime(false);
      setCustomStartTime('');
      setCustomEndTime('');
    } else {
      setSelectedHours('');
      setUseCustomTime(true);
      setCustomStartTime(shift.start_time);
      setCustomEndTime(shift.end_time);
    }
    
    setSelectedShiftType(shift.shift_type);
    setEditingShift(shift);
    setOpenModal(true);
  };

  // Function to determine shift type based on start time
  const determineShiftType = (startTime) => {
    const [hours] = startTime.split(':').map(Number);
    // Πρωινή βάρδια: 05:00 - 14:59
    // Απογευματινή βάρδια: 15:00 - 04:59
    if (hours >= 5 && hours < 15) {
      return 'morning';
    } else {
      return 'evening';
    }
  };

  const handleSaveShift = async () => {
    if (!selectedEmployee) {
      setError('Παρακαλώ επιλέξτε υπάλληλο');
      return;
    }

    // For editing mode, ensure we have a day selected
    if (editingShift && !selectedDay && selectedDays.length === 0) {
      setError('Παρακαλώ επιλέξτε μέρα');
      return;
    }

    // For new shifts, ensure at least one day is selected
    if (!editingShift && selectedDays.length === 0) {
      setError('Παρακαλώ επιλέξτε τουλάχιστον μία μέρα');
      return;
    }

    let startTime, endTime;
    
    // Validation pattern για 24ωρη μορφή
    const timePattern = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    
    // Έλεγχος αν έχουν συμπληρωθεί και τα δύο πεδία ώρας
    const hasValidStartTime = customStartTime && customStartTime.trim() !== '' && timePattern.test(customStartTime);
    const hasValidEndTime = customEndTime && customEndTime.trim() !== '' && timePattern.test(customEndTime);
    
    if (hasValidStartTime && hasValidEndTime) {
      startTime = customStartTime.trim();
      endTime = customEndTime.trim();
    } else if (customStartTime && !timePattern.test(customStartTime)) {
      setError('Η ώρα έναρξης πρέπει να είναι σε μορφή ΩΩ:ΛΛ (π.χ. 09:30)');
      return;
    } else if (customEndTime && !timePattern.test(customEndTime)) {
      setError('Η ώρα λήξης πρέπει να είναι σε μορφή ΩΩ:ΛΛ (π.χ. 17:30)');
      return;
    } else if (hasValidStartTime && !hasValidEndTime) {
      setError('Παρακαλώ συμπληρώστε και την ώρα λήξης');
      return;
    } else if (!hasValidStartTime && hasValidEndTime) {
      setError('Παρακαλώ συμπληρώστε και την ώρα έναρξης');
      return;
    } else if (selectedHours && selectedHours.trim() !== '') {
      [startTime, endTime] = selectedHours.split('-');
    } else {
      setError('Παρακαλώ συμπληρώστε ώρες έναρξης και λήξης ή επιλέξτε από τις προκαθορισμένες ώρες');
      return;
    }

    // Validate that end time is after start time
    // Convert time strings to minutes for comparison
    const timeToMinutes = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    };
    
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);
    
    // Allow overnight shifts (e.g., 23:00 to 07:00)
    // Only validate if both times are in the same day (no overnight)
    if (startMinutes >= endMinutes && endMinutes !== 0) {
      // If end time is very early (like 00:30, 01:00 etc.), it's probably overnight
      if (endMinutes < 8 * 60) { // Before 8:00 AM, likely overnight
        // Allow overnight shifts
      } else {
        setError('Η ώρα λήξης πρέπει να είναι μετά την ώρα έναρξης');
        return;
      }
    }

    const weekStart = currentWeek.format('YYYY-MM-DD');

    // Determine shift type based on start time
    const actualShiftType = determineShiftType(startTime);

    try {
      // If editing an existing shift, just update it
      if (editingShift) {
        // Convert UI selectedDay to backend day number
        const backendDay = selectedDay === 6 ? 0 : selectedDay + 1;
        
        const shiftData = {
          day: backendDay,
          employee_id: selectedEmployee,
          start_time: startTime,
          end_time: endTime,
          week_start: weekStart,
          shift_type: actualShiftType
        };

        const response = await fetch(`/api/shifts/${editingShift.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(shiftData),
        });

        if (!response.ok) throw new Error('Failed to update shift');
      } else {
        // For new shifts, create one for each selected day
        const promises = selectedDays.map(async (dayIndex) => {
          // Convert UI dayIndex to backend day number
          const backendDay = dayIndex === 6 ? 0 : dayIndex + 1;
          
          const shiftData = {
            day: backendDay,
            employee_id: selectedEmployee,
            start_time: startTime,
            end_time: endTime,
            week_start: weekStart,
            shift_type: actualShiftType
          };

          const response = await fetch('/api/shifts', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(shiftData),
          });

          if (!response.ok) throw new Error(`Failed to save shift for day ${dayIndex}`);
          return response.json();
        });

        await Promise.all(promises);
      }

      setOpenModal(false);
      fetchShifts();
      setError('');
    } catch (error) {
      console.error('Error saving shift:', error);
      setError('Σφάλμα αποθήκευσης βάρδιας');
    }
  };

  const handleDeleteShift = async (shiftId) => {
    try {
      // Find the shift to get its details
      const shift = shifts.find(s => s.id === shiftId);
      if (!shift) {
        setError('Η βάρδια δεν βρέθηκε');
        return;
      }

      // Check if there are payments for this shift's week and employee
      const weekStart = currentWeek.format('YYYY-MM-DD');
      const paymentsResponse = await fetch(
        `/api/employee-payments/allocations/${shift.employee_id}/${weekStart}`
      );
      
      if (paymentsResponse.ok) {
        const payments = await paymentsResponse.json();
        
        if (payments.length > 0) {
          // Calculate total paid amount
          const totalPaid = payments.reduce((sum, p) => sum + p.allocated_amount, 0);
          const employee = employees.find(emp => emp.id === shift.employee_id);
          const employeeName = employee ? employee.name : 'Υπάλληλος';
          
          // Show confirmation dialog
          const confirmMessage = `⚠️ ΠΡΟΣΟΧΗ!\n\n` +
            `Ο/Η ${employeeName} έχει πληρωθεί €${totalPaid.toFixed(2)} για αυτή την εβδομάδα.\n\n` +
            `Αν διαγράψεις αυτή τη βάρδια, θα διαγραφούν ΑΥΤΟΜΑΤΑ και όλες οι πληρωμές της εβδομάδας.\n\n` +
            `Θέλεις να συνεχίσεις;`;
          
          if (!window.confirm(confirmMessage)) {
            return; // User cancelled
          }
          
          // Delete all payments for this week
          for (const payment of payments) {
            await fetch(`/api/employee-payments/${payment.payment_id}`, {
              method: 'DELETE'
            });
          }
        }
      }

      // Now delete the shift
      const response = await fetch(`/api/shifts/${shiftId}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete shift');

      fetchShifts();
    } catch (error) {
      console.error('Error deleting shift:', error);
      setError('Σφάλμα διαγραφής βάρδιας');
    }
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? employee.name : 'Άγνωστος';
  };

  const navigateWeek = (direction) => {
    setCurrentWeek(prev => prev.add(direction, 'week'));
  };

  // Function to get shift count color based on number of shifts
  const getShiftCountColor = (shiftCount) => {
    if (shiftCount <= 2) return '#e3f2fd'; // Ανοιχτό μπλε (0-2 βάρδιες)
    if (shiftCount <= 4) return '#e8f5e8'; // Ανοιχτό πράσινο (3-4 βάρδιες)  
    if (shiftCount === 5) return '#4caf50'; // Πιο σκούρο πράσινο (5 βάρδιες)
    if (shiftCount === 6) return '#ff9800'; // Πορτοκαλί (6 βάρδιες)
    return '#f44336'; // Κόκκινο (7+ βάρδιες)
  };

  // Function to get text color based on background
  const getTextColor = (shiftCount) => {
    if (shiftCount <= 4) return '#000000'; // Μαύρο κείμενο για ανοιχτά χρώματα
    return '#ffffff'; // Άσπρο κείμενο για σκούρα χρώματα
  };

  // Function to count shifts for an employee in current week
  const getEmployeeShiftCount = (employeeId) => {
    return shifts.filter(shift => shift.employee_id === employeeId).length;
  };

  // Function to group employees by position
  const getEmployeesByPosition = () => {
    const groupedEmployees = {};
    employees.forEach(employee => {
      const position = employee.position || 'Χωρίς Πόστο';
      if (!groupedEmployees[position]) {
        groupedEmployees[position] = [];
      }
      groupedEmployees[position].push(employee);
    });
    return groupedEmployees;
  };

  // Νέα συνάρτηση για το νέο table layout
  const getEmployeeShiftsForDayAndType = (employeeId, dayIndex, shiftType) => {
    const dayShifts = getShiftsForDay(dayIndex);
    return dayShifts.filter(shift => 
      shift.employee_id === employeeId && 
      (
        (shiftType === 'morning' && (shift.shift_type === 'morning' || shift.shift_type === 'Πρωινη' || shift.shift_type === 'Εργάσιμη')) ||
        (shiftType === 'evening' && (shift.shift_type === 'evening' || shift.shift_type === 'Βραδινη' || shift.shift_type === 'Απογευματινή'))
      )
    );
  };

  if (loading) {
    return (
      <Box sx={{ maxWidth: '1600px', width: '100%', p: 3 }}>
        <Typography>Φόρτωση...</Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="el">
      <Box sx={{ maxWidth: '1600px', width: '100%' }}>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Πρόγραμμα Εργασίας 2 (Table View)
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton onClick={() => navigateWeek(-1)}>
                <NavigateBefore />
              </IconButton>
              <Typography variant="h6">
                {currentWeek.format('DD/MM/YYYY')} - {currentWeek.add(6, 'day').format('DD/MM/YYYY')}
              </Typography>
              <IconButton onClick={() => navigateWeek(1)}>
                <NavigateNext />
              </IconButton>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* New Table Layout */}
          <TableContainer 
            component={Paper} 
            variant="outlined" 
            sx={{ 
              overflow: 'auto', 
              width: '100%'
            }}
          >
            <Table sx={{ width: '100%', minWidth: 900, tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      fontWeight: 'bold', 
                      width: '70px',
                      minWidth: '70px',
                      maxWidth: '70px',
                      position: 'sticky',
                      left: 0,
                      backgroundColor: 'background.paper',
                      zIndex: 10
                    }}
                  >
                    Βάρδια
                  </TableCell>
                  {daysOfWeek.map((day, index) => (
                    <TableCell 
                      key={index} 
                      align="center" 
                      sx={{ 
                        fontWeight: 'bold', 
                        width: 'calc((100% - 70px) / 7)',
                        minWidth: '110px',
                        backgroundColor: '#f5f5f5',
                        padding: '8px 2px'
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.8rem' }}>{day}</Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                          {currentWeek.add(index, 'day').format('DD/MM')}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Morning Shifts Row */}
                <TableRow sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                  <TableCell 
                    sx={{ 
                      fontWeight: 'bold',
                      backgroundColor: '#e3f2fd',
                      position: 'sticky',
                      left: 0,
                      zIndex: 10,
                      width: '70px',
                      minWidth: '70px',
                      maxWidth: '70px'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontSize: '0.8rem' }}>Πρωινή</Typography>
                  </TableCell>
                  {daysOfWeek.map((day, dayIndex) => (
                    <TableCell 
                      key={dayIndex} 
                      sx={{ 
                        minHeight: 80,
                        verticalAlign: 'top',
                        padding: '4px 2px',
                        borderRight: '1px solid #e0e0e0',
                        width: 'calc((100% - 70px) / 7)'
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minHeight: 60 }}>
                        {employees.map(employee => {
                          const employeeShifts = getEmployeeShiftsForDayAndType(employee.id, dayIndex, 'morning');
                          return employeeShifts.map(shift => (
                            <Chip
                              key={shift.id}
                              label={`${employee.name.split(' ')[0]} (${shift.start_time}-${shift.end_time})`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              onClick={() => handleEditShift(shift)}
                              onDelete={() => handleDeleteShift(shift.id)}
                              deleteIcon={<Delete />}
                              sx={{ 
                                fontSize: '0.6rem',
                                height: 20,
                                cursor: 'pointer',
                                '& .MuiChip-label': {
                                  paddingLeft: 0.2,
                                  paddingRight: 0.2,
                                  fontSize: '0.6rem'
                                },
                                '& .MuiChip-deleteIcon': {
                                  width: 14,
                                  height: 14
                                }
                              }}
                            />
                          ));
                        })}
                        <IconButton
                          size="small"
                          onClick={() => handleAddShift(dayIndex, 'morning')}
                          sx={{ 
                            alignSelf: 'flex-start',
                            width: 16,
                            height: 16,
                            opacity: 0.6,
                            '&:hover': { opacity: 1 }
                          }}
                        >
                          <Add fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>

                {/* Evening Shifts Row */}
                <TableRow sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                  <TableCell 
                    sx={{ 
                      fontWeight: 'bold',
                      backgroundColor: '#fff3e0',
                      position: 'sticky',
                      left: 0,
                      zIndex: 10,
                      width: '70px',
                      minWidth: '70px',
                      maxWidth: '70px'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontSize: '0.8rem' }}>Βραδινή</Typography>
                  </TableCell>
                  {daysOfWeek.map((day, dayIndex) => (
                    <TableCell 
                      key={dayIndex} 
                      sx={{ 
                        minHeight: 80,
                        verticalAlign: 'top',
                        padding: '4px 2px',
                        borderRight: '1px solid #e0e0e0',
                        width: 'calc((100% - 70px) / 7)'
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minHeight: 60 }}>
                        {employees.map(employee => {
                          const employeeShifts = getEmployeeShiftsForDayAndType(employee.id, dayIndex, 'evening');
                          return employeeShifts.map(shift => (
                            <Chip
                              key={shift.id}
                              label={`${employee.name.split(' ')[0]} (${shift.start_time}-${shift.end_time})`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                              onClick={() => handleEditShift(shift)}
                              onDelete={() => handleDeleteShift(shift.id)}
                              deleteIcon={<Delete />}
                              sx={{ 
                                fontSize: '0.6rem',
                                height: 20,
                                cursor: 'pointer',
                                '& .MuiChip-label': {
                                  paddingLeft: 0.2,
                                  paddingRight: 0.2,
                                  fontSize: '0.6rem'
                                },
                                '& .MuiChip-deleteIcon': {
                                  width: 14,
                                  height: 14
                                }
                              }}
                            />
                          ));
                        })}
                        <IconButton
                          size="small"
                          onClick={() => handleAddShift(dayIndex, 'evening')}
                          sx={{ 
                            alignSelf: 'flex-start',
                            width: 16,
                            height: 16,
                            opacity: 0.6,
                            '&:hover': { opacity: 1 }
                          }}
                        >
                          <Add fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Add/Edit Shift Modal - same as original */}
          <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              {editingShift ? 'Επεξεργασία Βάρδιας' : 'Προσθήκη Βάρδιας'}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {/* Day Selection - Only show for new shifts, not when editing */}
                {!editingShift && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Επιλογή Ημερών:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {daysOfWeek.map((dayName, dayIndex) => (
                        <Chip
                          key={dayIndex}
                          label={dayName}
                          onClick={() => handleDayToggle(dayIndex)}
                          variant={selectedDays.includes(dayIndex) ? 'filled' : 'outlined'}
                          sx={{ 
                            cursor: 'pointer',
                            backgroundColor: selectedDays.includes(dayIndex) ? 'primary.main' : '#ffffff',
                            color: selectedDays.includes(dayIndex) ? '#ffffff' : 'text.primary',
                            border: selectedDays.includes(dayIndex) ? '2px solid' : '1px solid',
                            borderColor: selectedDays.includes(dayIndex) ? 'primary.main' : 'grey.300',
                            '&:hover': {
                              backgroundColor: selectedDays.includes(dayIndex) ? 'primary.dark' : 'grey.100',
                              opacity: 0.8
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Grid>
                )}
                
                {/* For editing, show the single day */}
                {editingShift && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      Μέρα: {daysOfWeek[selectedDay]}
                    </Typography>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Επιλογή Υπαλλήλου:
                  </Typography>
                  {Object.entries(getEmployeesByPosition()).map(([position, positionEmployees]) => (
                    <Box key={position} sx={{ mb: 2 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 'bold', 
                          color: 'primary.main',
                          mb: 1,
                          textTransform: 'uppercase',
                          fontSize: '0.875rem'
                        }}
                      >
                        {position}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, ml: 1 }}>
                        {positionEmployees.map((employee) => {
                          const shiftCount = getEmployeeShiftCount(employee.id);
                          const backgroundColor = getShiftCountColor(shiftCount);
                          const textColor = getTextColor(shiftCount);
                          
                          return (
                            <Chip
                              key={employee.id}
                              label={`${employee.name} | ${shiftCount}`}
                              onClick={() => setSelectedEmployee(employee.id)}
                              variant={selectedEmployee === employee.id ? 'filled' : 'outlined'}
                              sx={{ 
                                cursor: 'pointer',
                                backgroundColor: selectedEmployee === employee.id ? 'primary.main' : backgroundColor,
                                color: selectedEmployee === employee.id ? '#ffffff' : textColor,
                                border: selectedEmployee === employee.id ? '2px solid' : '1px solid',
                                borderColor: selectedEmployee === employee.id ? 'primary.main' : 'grey.300',
                                '&:hover': {
                                  backgroundColor: selectedEmployee === employee.id ? 'primary.dark' : backgroundColor,
                                  opacity: 0.8
                                }
                              }}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  ))}
                </Grid>
                
                {/* Πεδία για προσαρμοσμένες ώρες - εμφανίζονται πάντα */}
                <Grid item xs={6}>
                  <TextField
                    label="Ώρα έναρξης"
                    type="text"
                    value={customStartTime}
                    onChange={(e) => {
                      // Αυτόματη μορφοποίηση κατά την πληκτρολόγηση
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value += ':';
                      }
                      if (value.length <= 5) {
                        setCustomStartTime(value);
                        // Όταν εισάγει custom ώρες, καθαρίζουμε τις προκαθορισμένες
                        if (value && customEndTime) {
                          setSelectedHours('');
                        }
                      }
                    }}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      placeholder: "ΩΩ:ΛΛ",
                      maxLength: 5,
                      pattern: "^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$"
                    }}
                    sx={{
                      '& input': {
                        fontSize: '1rem',
                        fontFamily: 'monospace',
                        textAlign: 'center'
                      }
                    }}
                    helperText="24ωρη μορφή: 07:30, 09:00, 19:30"
                    error={customStartTime && !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(customStartTime)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Ώρα λήξης"
                    type="text"
                    value={customEndTime}
                    onChange={(e) => {
                      // Αυτόματη μορφοποίηση κατά την πληκτρολόγηση
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value += ':';
                      }
                      if (value.length <= 5) {
                        setCustomEndTime(value);
                        // Όταν εισάγει custom ώρες, καθαρίζουμε τις προκαθορισμένες
                        if (customStartTime && value) {
                          setSelectedHours('');
                        }
                      }
                    }}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      placeholder: "ΩΩ:ΛΛ",
                      maxLength: 5,
                      pattern: "^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$"
                    }}
                    sx={{
                      '& input': {
                        fontSize: '1rem',
                        fontFamily: 'monospace',
                        textAlign: 'center'
                      }
                    }}
                    helperText="24ωρη μορφή: 17:00, 18:30, 23:00"
                    error={customEndTime && !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(customEndTime)}
                  />
                </Grid>
                
                {/* Διαχωριστική γραμμή */}
                <Grid item xs={12} sx={{ textAlign: 'center', my: 1 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                    ή επιλέξτε από τις προκαθορισμένες ώρες:
                  </Typography>
                </Grid>
                
                {/* Προκαθορισμένες ώρες */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {presetHours.map((hours, index) => (
                      <Chip
                        key={index}
                        label={hours}
                        onClick={() => {
                          setSelectedHours(hours);
                          const [startTime, endTime] = hours.split('-');
                          setCustomStartTime(startTime);
                          setCustomEndTime(endTime);
                        }}
                        color={selectedHours === hours ? 'primary' : 'default'}
                        variant={selectedHours === hours ? 'filled' : 'outlined'}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenModal(false)}>
                Ακύρωση
              </Button>
              <Button onClick={handleSaveShift} variant="contained">
                {editingShift ? 'Ενημέρωση' : 'Προσθήκη'}
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default Schedule2;
